import { useCallback, useEffect, useRef, useState } from 'react'
import { getApiErrorMessage } from '../../../shared/api/apiClient'
import {
  EXTRACTION_STATUS,
  isExtractionRunning,
  toPollInterval,
} from '../config/brandLifecycleConfig'
import { brandExtractionService as sharedExtractionService } from '../services/brandExtractionService'

const NOT_STARTED = Object.freeze({
  documents: [],
  error: '',
  errorCode: '',
  jobId: '',
  pollAfterMs: 0,
  requestId: '',
  retryAvailable: false,
  reviewReady: false,
  status: EXTRACTION_STATUS.NOT_STARTED,
})

// Owns "Run AI extraction" and the poll that follows it. The extraction
// itself is opaque to the UI — everything it knows comes from the status
// endpoint, which answers for a brand that has never extracted too, so this
// is also what tells the Drafts tab whether it has anything to show.
export const useExtraction = ({
  brand,
  extractionService,
  isEnabled = true,
}) => {
  const service = extractionService || sharedExtractionService
  const [isStarting, setIsStarting] = useState(false)
  const [status, setStatus] = useState(NOT_STARTED)
  // The poll stops as soon as the job settles, so starting a new extraction
  // has to wake it back up. Bumping this re-runs the effect below, which is
  // what re-arms it.
  const [pollEpoch, setPollEpoch] = useState(0)
  // The poll reschedules itself rather than running on a fixed interval, so
  // the server's poll_after_ms is honoured and two reads never overlap.
  const timeoutRef = useRef(null)
  const isMountedRef = useRef(true)

  const readStatus = useCallback(async () => {
    if (!brand?.id) return NOT_STARTED
    try {
      const next = await service.getExtractionStatus(brand)
      if (isMountedRef.current) setStatus(next)
      return next
    } catch {
      // A failed poll is not worth surfacing: the next tick usually recovers,
      // and the screen still has the last good answer.
      return null
    }
  }, [brand, service])

  useEffect(() => {
    isMountedRef.current = true
    if (!isEnabled || !brand?.id) return undefined

    // A read that threw says nothing about the job, so the loop keeps going
    // and backs off instead of treating the silence as "finished".
    let consecutiveFailures = 0
    const tick = async () => {
      const next = await readStatus()
      if (!isMountedRef.current) return
      // Nothing more will happen on its own once the job is settled.
      if (next && !isExtractionRunning(next.status)) return
      consecutiveFailures = next ? 0 : consecutiveFailures + 1
      timeoutRef.current = setTimeout(
        tick,
        toPollInterval(next?.pollAfterMs, consecutiveFailures),
      )
    }
    tick()

    return () => {
      isMountedRef.current = false
      clearTimeout(timeoutRef.current)
    }
  }, [brand?.id, isEnabled, pollEpoch, readStatus])

  // Resolves to { started } or { error }. The message is returned rather than
  // held in state so the caller reads it in the same tick it acts on.
  const startExtraction = useCallback(async () => {
    setIsStarting(true)
    try {
      const started =
        status.retryAvailable && status.jobId
          ? await service.retryExtraction(brand, status.jobId)
          : await service.startExtraction(brand)
      // Re-arms the poll, which reads straight away — so the Drafts tab shows
      // the progress screen without waiting out an interval, and keeps
      // updating even though the loop had stopped on NOT_STARTED.
      setPollEpoch((current) => current + 1)
      return { started }
    } catch (requestError) {
      return {
        error: getApiErrorMessage(
          requestError,
          'AI extraction could not be started.',
        ),
      }
    } finally {
      setIsStarting(false)
    }
  }, [brand, service, status.jobId, status.retryAvailable])

  return { isStarting, startExtraction, status }
}
